// Iter 83.2 / Phase E4 Sprint 2 — Asset Categories Master.
// CRUD UI for asset categories. 12 system-seeded defaults (D17) are
// read-only on rename/delete but `is_active` may be toggled. Custom
// categories created by operators are fully editable.
import { useCallback, useEffect, useState } from "react";
import {
    Plus, PencilSimple, TrashSimple, MagnifyingGlass, Tag,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";

const EMPTY = { name: "", description: "", is_active: true };

export default function AssetCategoriesAdmin() {
    const { can } = useAuth();
    const canEdit = can("assets:admin");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/assets/category/list");
            setItems(data.items);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load categories"));
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { reload(); }, [reload]);

    const filtered = items.filter((it) => !search || (it.name || "").toLowerCase().includes(search.toLowerCase()));

    const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
    const openEdit = (it) => { setEditing(it); setForm(it); setOpen(true); };

    const save = async () => {
        if (!form.name?.trim()) { toast.error("Name is required"); return; }
        try {
            if (editing) {
                await api.patch(`/assets/category/${editing.id}`, {
                    name: editing.is_system_managed ? undefined : form.name,
                    description: form.description,
                    is_active: form.is_active,
                });
                toast.success(`Updated ${form.name}`);
            } else {
                await api.post("/assets/category", form);
                toast.success(`Created ${form.name}`);
            }
            setOpen(false);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Save failed"));
        }
    };
    const remove = async (it) => {
        if (!window.confirm(`Delete category '${it.name}'? Categories with referenced assets cannot be deleted.`)) return;
        try {
            await api.delete(`/assets/category/${it.id}`);
            toast.success(`Deleted ${it.name}`);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Delete failed"));
        }
    };

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1300px]" data-testid="asset-categories-page">
            <PageHeader
                eyebrow="Asset Management"
                title="Asset Categories"
                description={`${items.length} categor${items.length === 1 ? "y" : "ies"} · 12 system defaults seeded (D17)`}
                actions={canEdit ? (
                    <Button onClick={openNew} size="sm" data-testid="asset-cat-new-btn">
                        <Plus size={14} className="mr-1" /> New Category
                    </Button>
                ) : null}
            />
            <section className="mt-5 bg-white rounded-xl border border-slate-200 p-4">
                <div className="relative max-w-md">
                    <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input className="pl-9" placeholder="Search…" value={search}
                        onChange={(e) => setSearch(e.target.value)} data-testid="asset-cat-search" />
                </div>
            </section>
            <section className="mt-4 bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="asset-cat-table">
                {loading ? <div className="text-center py-12 text-sm text-slate-400">Loading…</div> : (
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600 text-xs">
                            <tr>
                                <th className="text-left px-3 py-2 font-medium">Name</th>
                                <th className="text-left px-3 py-2 font-medium">Description</th>
                                <th className="text-left px-3 py-2 font-medium">Type</th>
                                <th className="text-left px-3 py-2 font-medium">Active</th>
                                <th className="text-left px-3 py-2 font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((it) => (
                                <tr key={it.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`asset-cat-row-${it.name.replaceAll(" ", "-")}`}>
                                    <td className="px-3 py-2 font-medium">{it.name}</td>
                                    <td className="px-3 py-2 text-xs text-slate-600">{it.description || "—"}</td>
                                    <td className="px-3 py-2">
                                        {it.is_system_managed
                                            ? <Badge className="bg-slate-100 text-slate-600 text-[10px]">System</Badge>
                                            : <Badge className="bg-violet-100 text-violet-700 text-[10px]">Custom</Badge>}
                                    </td>
                                    <td className="px-3 py-2 text-xs">{it.is_active ? "Active" : <span className="text-slate-400">Inactive</span>}</td>
                                    <td className="px-3 py-2">
                                        <div className="flex gap-1">
                                            {canEdit ? (
                                                <Button variant="ghost" size="sm" onClick={() => openEdit(it)} data-testid={`asset-cat-edit-${it.name.replaceAll(" ", "-")}`}>
                                                    <PencilSimple size={12} />
                                                </Button>
                                            ) : null}
                                            {canEdit && !it.is_system_managed && (
                                                <Button variant="ghost" size="sm" onClick={() => remove(it)} data-testid={`asset-cat-del-${it.name.replaceAll(" ", "-")}`}>
                                                    <TrashSimple size={12} className="text-rose-600" />
                                                </Button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-md" data-testid="asset-cat-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2"><Tag size={18} /> {editing ? `Edit '${editing.name}'` : "New Category"}</DialogTitle>
                    </DialogHeader>
                    <div className="grid gap-3 text-sm">
                        <div>
                            <Label>Name *</Label>
                            <Input value={form.name}
                                disabled={editing?.is_system_managed}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                data-testid="asset-cat-form-name" />
                            {editing?.is_system_managed && (
                                <p className="text-[10px] text-slate-500 mt-1">System-seeded categories cannot be renamed.</p>
                            )}
                        </div>
                        <div>
                            <Label>Description</Label>
                            <Input value={form.description}
                                onChange={(e) => setForm({ ...form, description: e.target.value })} />
                        </div>
                        <label className="flex items-center gap-2 text-xs">
                            <input type="checkbox" checked={form.is_active}
                                onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                            Active
                        </label>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button onClick={save} data-testid="asset-cat-save">{editing ? "Save" : "Create"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
